// Copyright 2020 Drivetrain Hub LLC
// GraphQL handlers for library data (gears, tools, NOT models).

import {formatValue, isEmptyArr, objNumbers2Strings} from "../../assets/js/utils";
import {store} from "../../store";
import requests from "../../requests";
import {UNITS} from "./units";

// region TYPES

function isGearDetail(libObj) {
  return libObj.__typename === requests.gear.TYPENAME.GEAR_DETAIL;
}

function isFrame(libObj) {
  return libObj.__typename === requests.gear.TYPENAME.FRAME;
}

// endregion

// region SCALARS

function localeDatetime(utcDatetime, format = 'datetime') {
  // Convert and format the UTC datetime received from API.
  // Database stores date information according to ISO 8601 Datetime extended notation.
  // http://support.sas.com/documentation/cdl/en/lrdict/64316/HTML/default/viewer.htm#a003169814.htm
  // https://stackoverflow.com/questions/6525538/convert-utc-date-time-to-local-date-time

  const localDatetime = new Date(utcDatetime + 'Z');

  switch (format) {
    case 'full':
      // date, time, and local timezone
      // Thu Oct 17 2019 07:11:28 GMT-0600 (Mountain Daylight Time)
      return localDatetime.toString();
    case 'datetime':
      // date, time, and local timezone
      // 10/16/2019, 5:26:18 PM
      return localDatetime.toLocaleString();
    case 'date':
      // date
      // Wed Oct 16 2019
      return localDatetime.toDateString();
  }
}

/**
 * Format the name and number of a library object for display.
 * @param {Object} libObj - Object with name and number props.
 * @return {string} Formatted string of library object name, and optionally number.
 */
function nameNumStr(libObj) {
  return libObj.number ? libObj.name + ' | ' + libObj.number : libObj.name;
}

// endregion

// region TABLES

function mapShared(shared) {
  // Map array of user or organization Id to names.

  let name, names = '';
  shared.forEach(itemId => {
    try {
      name = store.getters.getUserFullName(itemId);
    } catch (e) {
      // if user not identified, check if item refers to current org
      if (itemId === store.state.session.user.organization.Id) {
        name = store.state.session.user.organization.name;
      }
    }
    names += `${name}, `;
  });

  return names;
}

function tableFile(libObj) {
  const shared = libObj.shared;

  if (libObj) {
    return [
      {
        parameter: 'Name',
        value: libObj.name,
      }, {
        parameter: 'Number',
        value: libObj.number,
      }, {
        parameter: 'Description',
        value: libObj.description,
      }, {
        parameter: 'Owner',
        value: store.getters.getUserFullName(libObj.owner),
      }, {
        parameter: 'Shared',
        value: isEmptyArr(shared) ? '-' : mapShared(shared),
      }, {
        parameter: 'Locked',
        value: formatValue(libObj.lock),
      }, {
        parameter: 'Last modified on',
        value: !libObj.lastModifiedOn ? '-' : localeDatetime(libObj.lastModifiedOn),
      }, {
        parameter: 'Last modified by',
        value: !libObj.lastModifiedBy ? '-' : store.getters.getUserFullName(libObj.lastModifiedBy),
      }, {
        parameter: 'Created on',
        value: !libObj.createdOn ? '-' : localeDatetime(libObj.createdOn),
      }, {
        parameter: 'Created by',
        value: !libObj.createdBy ? '-' : store.getters.getUserFullName(libObj.createdBy),
      }]
  } else {
    return [];
  }
}

function tableReleases() {
  // PLACEHOLDER {DRY} cleanup and implement this within ReportTab.vue to display release table
  //  - Also consider using within DialogModelInfo to display release table.
  //  - Releases for the master model are stored in store.state.model.releases
  const releases = [];

  let params = releases.map(release => {
    return {
      version: release.version,
      createdOn: release.createdOn,
      createdBy: store.getters.getUserFullName(release.createdBy),
      lastModifiedOn: release.lastModifiedOn,
      lastModifiedBy: store.getters.getUserFullName(release.lastModifiedBy),
      notes: release.notes,
    }
  });

  // OTHER TABLE PROPERTIES
  const title = 'Releases';
  const headings = ['Version', 'Released on', 'Released by', 'Notes'];
  const align =  ['center', 'left', 'left', 'left'];  // for data cells, not headings

  return {title: title, headings: headings, params: params, align: align};
}

function libTableParams(item, lookupInfoFcn, units={}) {
  const UNITS_LABEL = {...UNITS, ...units};
  let params = {};

  for (const [key, value] of Object.entries(item)) {
    try {
      // get param info
      let {info, index} = lookupInfoFcn(key);

      // add item to array with indexed key
      params[index] = {
        parameter: info.desc,
        symbol: info.symbol,
        value: formatValue(value),
        units: UNITS_LABEL[info.units],
      };
    } catch (e) {
      // do nothing - error probably caused by param not exist
      // console.warn('key: ' + key, e);
    }
  }

  // map params obj to arr, ordered per indexed keys
  return Object.values(params);
}

// endregion

// region FORMS

function libObjectForms(
  {name, number, description, shared, lock, lastModifiedOn, lastModifiedBy, createdOn, createdBy, geom, ...rest}) {
  // Map library object to a data structure used by forms to create and edit the object.
  // Returns array of [info, sharing, signed, geom, rest]

  const info = {
    name: name,
    number: number,
    description: description,
  };

  const sharing = {
    shared: shared,
    lock: lock,
  };

  const signed = {
    lastModifiedOn: lastModifiedOn,
    lastModifiedBy: lastModifiedBy,
    createdOn: createdOn,
    createdBy: createdBy,
  };

  // convert geom numbers to strings to avoid form validation problems with the number zero
  delete geom.__typename;
  objNumbers2Strings(geom);

  return [info, sharing, signed, geom, rest]
}

// endregion

export default {
  isGearDetail: isGearDetail,
  isFrame: isFrame,
  localeDatetime: localeDatetime,
  nameNumStr: nameNumStr,
  tableFile: tableFile,
  tableReleases: tableReleases,
  libTableParams: libTableParams,
  libObjectForms: libObjectForms,
}
